import React, { useEffect, useMemo, useState } from 'react';
import { Calendar as CalendarIcon, Clock, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const VARIANTS_KEY = 'consistency_optimized_variants';
const SCHEDULED_POSTS_KEY = 'consistency_scheduled_posts';

const Scheduler = () => {
  const [variants, setVariants] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [platform, setPlatform] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [hashtags, setHashtags] = useState('');
  
  useEffect(() => {
        const storedVariants = localStorage.getItem(VARIANTS_KEY);
    const parsedVariants = storedVariants ? JSON.parse(storedVariants) : [];
    setVariants(parsedVariants);

    const storedPosts = localStorage.getItem(SCHEDULED_POSTS_KEY);
    setScheduledPosts(storedPosts ? JSON.parse(storedPosts) : []);
  }, []);

  const readyVariants = useMemo(() => variants.filter((variant) => variant.status === 'ready'), [variants]);

  useEffect(() => {  
    if (selectedVariantId) {
      const variant = variants.find((v) => v.id === selectedVariantId);
      if (variant) {
        setPlatform(variant.platforms?.[0] || '');
        setTitle(variant.title || '');
        setDescription(variant.description || '');
        setHashtags(variant.hashtags || '');
      }    
    }
  }, [selectedVariantId, variants]);
  
  const persistVariants = (updated) => {
    setVariants(updated);
    localStorage.setItem(VARIANTS_KEY, JSON.stringify(updated));
  };
  
  const persistScheduledPosts = (updated) => {
    setScheduledPosts(updated);
    localStorage.setItem(SCHEDULED_POSTS_KEY, JSON.stringify(updated));
  };
  
  const handleSchedulePost = (e) => {
    e.preventDefault();  
    const variant = variants.find((v) => v.id === selectedVariantId);
    if (!variant) {
      toast.error('Please select an optimized variant');
      return;
    }

    if (!scheduledTime) {
      toast.error('Please choose a time');
      return;
    }

    const scheduledAt = new Date(scheduledTime).toISOString();
    const newPost = {
      id: `schedule_${Date.now()}`,
      variantId: variant.id,
      platform: platform || variant.platforms?.[0] || 'unspecified',
      scheduledAt,
      title,
      description,
      hashtags,
      createdAt: new Date().toISOString()
    };
    
    const updatedPosts = [...scheduledPosts, newPost];
    persistScheduledPosts(updatedPosts);

    const updatedVariants = variants.map((v) =>
      v.id === variant.id ? { ...v, status: 'scheduled' } : v
    );
    persistVariants(updatedVariants);
    setSelectedVariantId('');
    setPlatform('');
    setScheduledTime('');
    setTitle('');
    setDescription('');
    setHashtags('');
    toast.success('Post scheduled');
  };

  return (    
    <div className="space-y-6">
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Scheduler (ready variants only)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {readyVariants.length === 0 ? (
            <p className="text-sm text-[#8A93A6]">No ready variants available. Create and ready variants in Optimization first.</p>
          ) : (
            <form onSubmit={handleSchedulePost} className="space-y-4">
              <div>
                <Label>Variant</Label>
                <Select value={selectedVariantId} onValueChange={setSelectedVariantId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a ready variant" />
                  </SelectTrigger>
                  <SelectContent>
                    {readyVariants.map((variant) => (
                      <SelectItem key={variant.id} value={variant.id}>
                        {variant.title}
                      </SelectItem>
                    ))}    
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Platform</Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose platform" />
                    </SelectTrigger>
                    <SelectContent>
                      {(variants.find((v) => v.id === selectedVariantId)?.platforms || []).map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Schedule Time</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#8A93A6]" />
                    <Input type="datetime-local" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} />
                  </div>
                </div>
              </div>

              <div>
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>          
              <div>
                <Label>Description</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>       
              <div>
                <Label>Hashtags</Label>
                <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} />
              </div>
              
              <Button type="submit" className="w-full" disabled={!selectedVariantId}>
                <Plus className="h-4 w-4 mr-2" />
                Schedule Post
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="glass">
        <CardHeader>
          <CardTitle>Scheduled Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {scheduledPosts.length === 0 ? (
            <p className="text-sm text-[#8A93A6]">No posts scheduled yet.</p>
          ) : (
            <div className="space-y-3">
              {scheduledPosts.map((post) => (
                <div key={post.id} className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{post.title || 'Untitled post'}</p>
                      <p className="text-xs text-[#8A93A6]">Variant: {post.variantId}</p>
                      <p className="text-xs text-[#8A93A6]">Platform: {post.platform}</p>
                    </div>
                    <span className="text-xs text-[#8A93A6]">{new Date(post.scheduledAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Scheduler;                                